/**
 * Common form constants used across all forms in the application
 * This ensures consistency and makes maintenance easier
 */

// ID Types
export const ID_TYPES = {
    MYKAD: 'MYKAD',
    PASSPORT: 'PASSPORT'
};

// ID Type display names
export const ID_TYPE_DISPLAY_NAMES = {
    [ID_TYPES.MYKAD]: 'MyKad',
    [ID_TYPES.PASSPORT]: 'Passport'
};

// ID Type options for select dropdowns
export const ID_TYPE_OPTIONS = [
    {value: ID_TYPES.MYKAD, label: ID_TYPE_DISPLAY_NAMES[ID_TYPES.MYKAD]},
    {value: ID_TYPES.PASSPORT, label: ID_TYPE_DISPLAY_NAMES[ID_TYPES.PASSPORT]}
];

// Gender options
export const GENDER_OPTIONS = {
    MALE: 'MALE',
    FEMALE: 'FEMALE',
    OTHER: 'OTHER',
    NOT_SPECIFIED: 'NOT_SPECIFIED'
};

// Gender display names
export const GENDER_DISPLAY_NAMES = {
    [GENDER_OPTIONS.MALE]: 'Male',
    [GENDER_OPTIONS.FEMALE]: 'Female',
    [GENDER_OPTIONS.OTHER]: 'Other',
    [GENDER_OPTIONS.NOT_SPECIFIED]: 'Not Specified'
};

// Gender options for select dropdowns
export const GENDER_SELECT_OPTIONS = [
    {value: GENDER_OPTIONS.MALE, label: GENDER_DISPLAY_NAMES[GENDER_OPTIONS.MALE]},
    {value: GENDER_OPTIONS.FEMALE, label: GENDER_DISPLAY_NAMES[GENDER_OPTIONS.FEMALE]},
    {value: GENDER_OPTIONS.OTHER, label: GENDER_DISPLAY_NAMES[GENDER_OPTIONS.OTHER]},
    {value: GENDER_OPTIONS.NOT_SPECIFIED, label: GENDER_DISPLAY_NAMES[GENDER_OPTIONS.NOT_SPECIFIED]}
];

// Staff Designation options
export const STAFF_DESIGNATION_OPTIONS = {
    TEACHER: 'TEACHER',
    CARE_TAKER: 'CARE_TAKER',
    HELPER: 'HELPER',
    SUPERVISOR: 'SUPERVISOR'
};

// Staff Designation display names
export const STAFF_DESIGNATION_DISPLAY_NAMES = {
    [STAFF_DESIGNATION_OPTIONS.TEACHER]: 'Teacher',
    [STAFF_DESIGNATION_OPTIONS.CARE_TAKER]: 'Care Taker',
    [STAFF_DESIGNATION_OPTIONS.HELPER]: 'Helper',
    [STAFF_DESIGNATION_OPTIONS.SUPERVISOR]: 'Supervisor'
};

// Staff Designation options for select dropdowns
export const STAFF_DESIGNATION_SELECT_OPTIONS = [
    {
        value: STAFF_DESIGNATION_OPTIONS.TEACHER,
        label: STAFF_DESIGNATION_DISPLAY_NAMES[STAFF_DESIGNATION_OPTIONS.TEACHER]
    },
    {
        value: STAFF_DESIGNATION_OPTIONS.CARE_TAKER,
        label: STAFF_DESIGNATION_DISPLAY_NAMES[STAFF_DESIGNATION_OPTIONS.CARE_TAKER]
    },
    {
        value: STAFF_DESIGNATION_OPTIONS.HELPER,
        label: STAFF_DESIGNATION_DISPLAY_NAMES[STAFF_DESIGNATION_OPTIONS.HELPER]
    },
    {
        value: STAFF_DESIGNATION_OPTIONS.SUPERVISOR,
        label: STAFF_DESIGNATION_DISPLAY_NAMES[STAFF_DESIGNATION_OPTIONS.SUPERVISOR]
    }
];

// Guardian Relationship options
export const GUARDIAN_RELATIONSHIP_OPTIONS = {
    FATHER: 'FATHER',
    MOTHER: 'MOTHER',
    GUARDIAN: 'GUARDIAN',
    OTHER: 'OTHER'
};

// Guardian Relationship display names
export const GUARDIAN_RELATIONSHIP_DISPLAY_NAMES = {
    [GUARDIAN_RELATIONSHIP_OPTIONS.FATHER]: 'Father',
    [GUARDIAN_RELATIONSHIP_OPTIONS.MOTHER]: 'Mother',
    [GUARDIAN_RELATIONSHIP_OPTIONS.GUARDIAN]: 'Guardian',
    [GUARDIAN_RELATIONSHIP_OPTIONS.OTHER]: 'Other'
};

// Guardian Relationship options for select dropdowns
export const GUARDIAN_RELATIONSHIP_SELECT_OPTIONS = [
    {
        value: GUARDIAN_RELATIONSHIP_OPTIONS.FATHER,
        label: GUARDIAN_RELATIONSHIP_DISPLAY_NAMES[GUARDIAN_RELATIONSHIP_OPTIONS.FATHER]
    },
    {
        value: GUARDIAN_RELATIONSHIP_OPTIONS.MOTHER,
        label: GUARDIAN_RELATIONSHIP_DISPLAY_NAMES[GUARDIAN_RELATIONSHIP_OPTIONS.MOTHER]
    },
    {
        value: GUARDIAN_RELATIONSHIP_OPTIONS.GUARDIAN,
        label: GUARDIAN_RELATIONSHIP_DISPLAY_NAMES[GUARDIAN_RELATIONSHIP_OPTIONS.GUARDIAN]
    },
    {
        value: GUARDIAN_RELATIONSHIP_OPTIONS.OTHER,
        label: GUARDIAN_RELATIONSHIP_DISPLAY_NAMES[GUARDIAN_RELATIONSHIP_OPTIONS.OTHER]
    }
];

// User Status options
export const USER_STATUS_OPTIONS = {
    ACTIVE: 'ACTIVE',
    IDLE: 'IDLE',
    INACTIVE: 'INACTIVE'
};

// User Status display names
export const USER_STATUS_DISPLAY_NAMES = {
    [USER_STATUS_OPTIONS.ACTIVE]: 'Active',
    [USER_STATUS_OPTIONS.IDLE]: 'Idle',
    [USER_STATUS_OPTIONS.INACTIVE]: 'Inactive'
};

// User Status options for select dropdowns
export const USER_STATUS_SELECT_OPTIONS = [
    {value: USER_STATUS_OPTIONS.ACTIVE, label: USER_STATUS_DISPLAY_NAMES[USER_STATUS_OPTIONS.ACTIVE]},
    {value: USER_STATUS_OPTIONS.IDLE, label: USER_STATUS_DISPLAY_NAMES[USER_STATUS_OPTIONS.IDLE]},
    {value: USER_STATUS_OPTIONS.INACTIVE, label: USER_STATUS_DISPLAY_NAMES[USER_STATUS_OPTIONS.INACTIVE]}
];

// User Role options
export const USER_ROLE_OPTIONS = {
    SYSTEM_ADMIN: 'SYSTEM_ADMIN',
    ADMIN: 'ADMIN',
    BRANCH_ADMIN: 'BRANCH_ADMIN',
    STAFF: 'STAFF',
    GUARDIAN: 'GUARDIAN'
};

// User Role display names
export const USER_ROLE_DISPLAY_NAMES = {
    [USER_ROLE_OPTIONS.SYSTEM_ADMIN]: 'System Admin',
    [USER_ROLE_OPTIONS.ADMIN]: 'Admin',
    [USER_ROLE_OPTIONS.BRANCH_ADMIN]: 'Branch Admin',
    [USER_ROLE_OPTIONS.STAFF]: 'Staff',
    [USER_ROLE_OPTIONS.GUARDIAN]: 'Guardian'
};

// Child Status options
export const CHILD_STATUS_OPTIONS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE'
};

// Child Status display names
export const CHILD_STATUS_DISPLAY_NAMES = {
    [CHILD_STATUS_OPTIONS.ACTIVE]: 'Active',
    [CHILD_STATUS_OPTIONS.INACTIVE]: 'Inactive'
};

// Child Status options for select dropdowns
export const CHILD_STATUS_SELECT_OPTIONS = [
    {value: CHILD_STATUS_OPTIONS.ACTIVE, label: CHILD_STATUS_DISPLAY_NAMES[CHILD_STATUS_OPTIONS.ACTIVE]},
    {value: CHILD_STATUS_OPTIONS.INACTIVE, label: CHILD_STATUS_DISPLAY_NAMES[CHILD_STATUS_OPTIONS.INACTIVE]}
];

// Company Status options - uses same enum as USER_STATUS_OPTIONS
export const COMPANY_STATUS_OPTIONS = USER_STATUS_OPTIONS;

// Company Status display names - uses same as USER_STATUS_DISPLAY_NAMES
export const COMPANY_STATUS_DISPLAY_NAMES = USER_STATUS_DISPLAY_NAMES;

// Company Status options for select dropdowns - uses same as USER_STATUS_SELECT_OPTIONS
export const COMPANY_STATUS_SELECT_OPTIONS = USER_STATUS_SELECT_OPTIONS;

// Helper functions
export const getDisplayName = (value, options, displayNames) => {
    return displayNames[value] || value || 'N/A';
};

export const getIdTypeDisplayName = (idType) => {
    return getDisplayName(idType, ID_TYPES, ID_TYPE_DISPLAY_NAMES);
};

export const getGenderDisplayName = (gender) => {
    return getDisplayName(gender, GENDER_OPTIONS, GENDER_DISPLAY_NAMES);
};

export const getStaffDesignationDisplayName = (designation) => {
    return getDisplayName(designation, STAFF_DESIGNATION_OPTIONS, STAFF_DESIGNATION_DISPLAY_NAMES);
};

export const getGuardianRelationshipDisplayName = (relationship) => {
    return getDisplayName(relationship, GUARDIAN_RELATIONSHIP_OPTIONS, GUARDIAN_RELATIONSHIP_DISPLAY_NAMES);
};

export const getUserStatusDisplayName = (status) => {
    return getDisplayName(status, USER_STATUS_OPTIONS, USER_STATUS_DISPLAY_NAMES);
};

export const getUserRoleDisplayName = (role) => {
    return getDisplayName(role, USER_ROLE_OPTIONS, USER_ROLE_DISPLAY_NAMES);
};

export const getChildStatusDisplayName = (status) => {
    return getDisplayName(status, CHILD_STATUS_OPTIONS, CHILD_STATUS_DISPLAY_NAMES);
};

export const getCompanyStatusDisplayName = (status) => {
    return getUserStatusDisplayName(status);
};

// Export all constants as a single object for easy importing
export const FORM_CONSTANTS = {
    ID_TYPES,
    ID_TYPE_DISPLAY_NAMES,
    ID_TYPE_OPTIONS,
    GENDER_OPTIONS,
    GENDER_DISPLAY_NAMES,
    GENDER_SELECT_OPTIONS,
    STAFF_DESIGNATION_OPTIONS,
    STAFF_DESIGNATION_DISPLAY_NAMES,
    STAFF_DESIGNATION_SELECT_OPTIONS,
    GUARDIAN_RELATIONSHIP_OPTIONS,
    GUARDIAN_RELATIONSHIP_DISPLAY_NAMES,
    GUARDIAN_RELATIONSHIP_SELECT_OPTIONS,
    USER_STATUS_OPTIONS,
    USER_STATUS_DISPLAY_NAMES,
    USER_STATUS_SELECT_OPTIONS,
    USER_ROLE_OPTIONS,
    USER_ROLE_DISPLAY_NAMES,
    CHILD_STATUS_OPTIONS,
    CHILD_STATUS_DISPLAY_NAMES,
    CHILD_STATUS_SELECT_OPTIONS,
    COMPANY_STATUS_OPTIONS,
    COMPANY_STATUS_DISPLAY_NAMES,
    COMPANY_STATUS_SELECT_OPTIONS
};
